import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AxiosError } from 'axios';
import { MailCheck } from 'lucide-react';
import { platformAuthService } from '@services/platform-auth.service';
import { PlatformAuthTabs } from './PlatformAuthTabs';

type ApiError = AxiosError<{ message?: string; error?: { code?: string; message?: string } | string }>;

/** Message to show for a failed registration request. */
export function platformRegistrationError(err: unknown): string {
  const axiosError = err as ApiError;
  const status = axiosError.response?.status;
  const data = axiosError.response?.data;
  const nested = typeof data?.error === 'object' ? data.error?.message : undefined;
  if (status === 429) return 'Too many attempts. Please wait a few minutes and try again.';
  if (nested) return nested;
  if (status && status < 500 && data?.message) return data.message;
  return 'Something went wrong. Please try again.';
}

/**
 * Platform-admin registration, step 1: email + registration token. On success
 * a verification link is emailed; the account is only created after the link
 * is opened (PlatformRegisterCompletePage).
 */
export default function PlatformRegisterPage() {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(true);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await platformAuthService.startRegistration(email.trim(), token.trim());
      setSentTo(response.maskedEmail || email.trim());
      setEmailSent(response.emailSent !== false);
    } catch (err) {
      setError(platformRegistrationError(err));
    } finally {
      setLoading(false);
    }
  };

  if (sentTo) {
    return (
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-[12px] bg-[var(--co-brass-wash)] mb-3">
          <MailCheck className="w-6 h-6 text-[var(--co-brass-ink)]" />
        </div>
        <h2 className="font-display text-[22px] font-extrabold tracking-[-0.02em] text-[var(--co-ink)]">
          Check your email
        </h2>
        {emailSent ? (
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            We sent a verification link to{' '}
            <span className="font-medium text-gray-800 dark:text-gray-200">{sentTo}</span>. Open it within 30
            minutes to set your name and password.
          </p>
        ) : (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">
            Your token was accepted, but the verification email could not be sent. Please try again in a
            minute, or contact the Main Super Admin.
          </p>
        )}
        <button
          type="button"
          onClick={() => setSentTo(null)}
          className="btn-secondary w-full !rounded-[10px] h-11 mt-6 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
        >
          Use a different email
        </button>
        <p className="mt-4 text-sm">
          <Link to="/platform-admin" className="text-[var(--co-brass-ink)] hover:text-[var(--co-brass)] font-medium">
            Back to Login
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 text-center">
        <h2 className="font-display text-[22px] font-extrabold tracking-[-0.02em] text-[var(--co-ink)]">
          Register as Platform Admin
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Enter your email and the registration token you were given
        </p>
      </div>

      <PlatformAuthTabs />

      <form className="space-y-6" onSubmit={handleSubmit}>
        {error && (
          <div className="px-4 py-3 rounded-[10px] bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label htmlFor="register-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Email address
            </label>
            <input
              id="register-email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input !rounded-[10px] mt-1 dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
              placeholder="The email the token was issued for"
            />
          </div>

          <div>
            <label htmlFor="register-token" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Registration token
            </label>
            <input
              id="register-token"
              name="token"
              type="text"
              autoComplete="off"
              spellCheck={false}
              required
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="input !rounded-[10px] mt-1 font-mono dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
              placeholder="PA-REG-…"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full !rounded-[10px] h-11 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Checking…' : 'Send verification email'}
        </button>
      </form>
    </div>
  );
}
